import { lazy, Suspense } from 'react';
import { Outlet } from 'react-router-dom';
// auth
import { AuthGuard, RoleBasedGuard } from 'src/auth/guard';
// layouts
import DashboardLayout from 'src/layouts/dashboard';
// components
import { LoadingScreen } from 'src/components/loading-screen';

// ----------------------------------------------------------------------

// OVERVIEW
const IndexPage = lazy(() => import('src/pages/dashboard/app'));
const OverviewEcommercePage = lazy(() => import('src/pages/dashboard/ecommerce'));
const OverviewAnalyticsPage = lazy(() => import('src/pages/dashboard/analytics'));
const OverviewBankingPage = lazy(() => import('src/pages/dashboard/banking'));
const OverviewBookingPage = lazy(() => import('src/pages/dashboard/booking'));
const OverviewFilePage = lazy(() => import('src/pages/dashboard/file'));
// PRODUCT
const ProductDetailsPage = lazy(() => import('src/pages/dashboard/product/details'));
const ProductListPage = lazy(() => import('src/pages/dashboard/product/list'));
const ProductCreatePage = lazy(() => import('src/pages/dashboard/product/new'));
const ProductEditPage = lazy(() => import('src/pages/dashboard/product/edit'));
// PRODUCT LOGS
const ProductLogListPage = lazy(() => import('src/pages/dashboard/productlog/list'));
// PRODUCT-CATEGORY
const ProductCategoryDetailsPage = lazy(() => import('src/pages/dashboard/product-category/details'));
const ProductCategoryListPage = lazy(() => import('src/pages/dashboard/product-category/list'));
const ProductCategoryCreatePage = lazy(() => import('src/pages/dashboard/product-category/new'));
const ProductCategoryEditPage = lazy(() => import('src/pages/dashboard/product-category/edit'));
// INVENTORY
const InventoryDetailsPage = lazy(() => import('src/pages/dashboard/inventory/details'));
const InventoryListPage = lazy(() => import('src/pages/dashboard/inventory/list'));
const InventoryCreatePage = lazy(() => import('src/pages/dashboard/inventory/new'));
const InventoryEditPage = lazy(() => import('src/pages/dashboard/inventory/edit'));
// INVENTORY LOGS
const InventoryLogListPage = lazy(() => import('src/pages/dashboard/inventorylog/list'));
// FETCH-LIST
const FetchListDetailsPage = lazy(() => import('src/pages/dashboard/fetchlist/details'));
const FetchListPage = lazy(() => import('src/pages/dashboard/fetchlist/list'));
const FetchListCreatePage = lazy(() => import('src/pages/dashboard/fetchlist/new'));
const FetchListEditPage = lazy(() => import('src/pages/dashboard/fetchlist/edit'));
const FetchListScanPage = lazy(() => import('src/pages/dashboard/fetchlist/scan'));
// RDER LOGS
const OrderLogListPage = lazy(() => import('src/pages/dashboard/orderlog/list'));
// ORDER
const OrderListPage = lazy(() => import('src/pages/dashboard/order/list'));
const OrderDetailsPage = lazy(() => import('src/pages/dashboard/order/details'));
const OrderCreatePage = lazy(() => import('src/pages/dashboard/order/new'));
const OrderEditPage = lazy(() => import('src/pages/dashboard/order/edit'));
// INVOICE
const InvoiceListPage = lazy(() => import('src/pages/dashboard/invoice/list'));
const InvoiceDetailsPage = lazy(() => import('src/pages/dashboard/invoice/details'));
const InvoiceCreatePage = lazy(() => import('src/pages/dashboard/invoice/new'));
const InvoiceEditPage = lazy(() => import('src/pages/dashboard/invoice/edit'));
// USER
const UserProfilePage = lazy(() => import('src/pages/dashboard/user/profile'));
const UserCardsPage = lazy(() => import('src/pages/dashboard/user/cards'));
const UserListPage = lazy(() => import('src/pages/dashboard/user/list'));
const UserAccountPage = lazy(() => import('src/pages/dashboard/user/account'));
const UserCreatePage = lazy(() => import('src/pages/dashboard/user/new'));
const UserEditPage = lazy(() => import('src/pages/dashboard/user/edit'));
// ORGANISATION
const OrganizationListPage = lazy(() => import('src/pages/dashboard/organisation/list'));
const OrganizationCreatePage = lazy(() => import('src/pages/dashboard/organisation/new'));
const OrganizationEditPage = lazy(() => import('src/pages/dashboard/organisation/edit'));
// BLOG
const BlogPostsPage = lazy(() => import('src/pages/dashboard/post/list'));
const BlogPostPage = lazy(() => import('src/pages/dashboard/post/details'));
const BlogNewPostPage = lazy(() => import('src/pages/dashboard/post/new'));
const BlogEditPostPage = lazy(() => import('src/pages/dashboard/post/edit'));
// JOB
const JobDetailsPage = lazy(() => import('src/pages/dashboard/job/details'));
const JobListPage = lazy(() => import('src/pages/dashboard/job/list'));
const JobCreatePage = lazy(() => import('src/pages/dashboard/job/new'));
const JobEditPage = lazy(() => import('src/pages/dashboard/job/edit'));
// TOUR
const TourDetailsPage = lazy(() => import('src/pages/dashboard/tour/details'));
const TourListPage = lazy(() => import('src/pages/dashboard/tour/list'));
const TourCreatePage = lazy(() => import('src/pages/dashboard/tour/new'));
const TourEditPage = lazy(() => import('src/pages/dashboard/tour/edit'));
// FILE MANAGER
const FileManagerPage = lazy(() => import('src/pages/dashboard/file-manager'));
// APP
const ChatPage = lazy(() => import('src/pages/dashboard/chat'));
const MailPage = lazy(() => import('src/pages/dashboard/mail'));
const CalendarPage = lazy(() => import('src/pages/dashboard/calendar'));
const KanbanPage = lazy(() => import('src/pages/dashboard/kanban'));
// TEST RENDER PAGE BY ROLE
const PermissionDeniedPage = lazy(() => import('src/pages/dashboard/permission'));
// BLANK PAGE
const BlankPage = lazy(() => import('src/pages/dashboard/blank'));
const ClientPage = lazy(() => import('src/pages/dashboard/client'));
const VendorPage = lazy(() => import('src/pages/dashboard/vendor'));
const PageConfigurationPage = lazy(() => import('src/pages/dashboard/page-configuration'));
const WorkerPage = lazy(() => import('src/pages/dashboard/worker'));
const MeasurementPage = lazy(() => import('src/pages/dashboard/measurement'));
// QR CODE
const QRCodeListPage = lazy(() => import('src/pages/dashboard/QRCode/list'));
// PROCESS
const ProcessListPage = lazy(() => import('src/pages/dashboard/process/list'));
const ProcessCreatePage = lazy(() => import('src/pages/dashboard/process/new'));
const ProcessEditPage = lazy(() => import('src/pages/dashboard/process/edit'));
// WORK ORDER
const WorkOrderListPage = lazy(() => import('src/pages/dashboard/workorder/list'));
const WorkOrderCreatePage = lazy(() => import('src/pages/dashboard/workorder/new'));
const WorkOrderEditPage = lazy(() => import('src/pages/dashboard/workorder/edit'));
const WorkOrderScanPage = lazy(() => import('src/pages/dashboard/workorder/scan'));

// NOTIFICATION
const NotificationListPage = lazy(() => import('src/pages/dashboard/notification/list'));

// ----------------------------------------------------------------------

export const dashboardRoutes = [
  {
    path: 'dashboard',
    element: (
      <AuthGuard>
        <RoleBasedGuard roles={['Admin']} hasContent>
          <DashboardLayout>
            <Suspense fallback={<LoadingScreen />}>
              <Outlet />
            </Suspense>
          </DashboardLayout>
        </RoleBasedGuard>
      </AuthGuard>
    ),
    children: [
      { element: <IndexPage />, index: true },
      { path: 'ecommerce', element: <OverviewEcommercePage /> },
      { path: 'analytics', element: <OverviewAnalyticsPage /> },
      { path: 'banking', element: <OverviewBankingPage /> },
      { path: 'booking', element: <OverviewBookingPage /> },
      { path: 'file', element: <OverviewFilePage /> },
      {
        path: 'user',
        children: [
          { element: <UserProfilePage />, index: true },
          { path: 'profile', element: <UserProfilePage /> },
          { path: 'cards', element: <UserCardsPage /> },
          { path: 'list', element: <UserListPage /> },
          { path: 'new', element: <UserCreatePage /> },
          { path: ':id/edit', element: <UserEditPage /> },
          { path: 'account', element: <UserAccountPage /> },
        ],
      },
      {
        path: 'organisation',
        children: [
          { element: <OrganizationListPage />, index: true },
          { path: 'list', element: <OrganizationListPage /> },
          { path: 'new', element: <OrganizationCreatePage /> },
          { path: ':id/edit', element: <OrganizationEditPage /> },
        ],
      },
      {
        path: 'product',
        children: [
          { element: <ProductListPage />, index: true },
          { path: 'list', element: <ProductListPage /> },
          { path: ':id', element: <ProductDetailsPage /> },
          { path: 'new', element: <ProductCreatePage /> },
          { path: ':id/edit', element: <ProductEditPage /> },
        ],
      },
      {
        path: 'productlog',
        children: [
          { element: <ProductLogListPage />, index: true },
          { path: 'list', element: <ProductLogListPage /> },
        ],
      },
        {
        path: 'notification',
        children: [
          { element: <NotificationListPage />, index: true },
          { path: 'list', element: <NotificationListPage /> },
        ],
      },
      {
        path: 'productcategory',
        children: [
          { element: <ProductCategoryListPage />, index: true },
          { path: 'list', element: <ProductCategoryListPage /> },
          { path: ':id', element: <ProductCategoryDetailsPage /> },
          { path: 'new', element: <ProductCategoryCreatePage /> },
          { path: ':id/edit', element: <ProductCategoryEditPage /> },
        ],
      },
      {
        path: 'qrcode',
        children: [
          { element: <QRCodeListPage />, index: true },
          { path: 'new', element: <QRCodeListPage /> },

        ],
      },
      {
        path: 'inventory',
        children: [
          { element: <InventoryListPage />, index: true },
          { path: 'list', element: <InventoryListPage /> },
          { path: ':id', element: <InventoryDetailsPage /> },
          { path: 'new', element: <InventoryCreatePage /> },
          { path: ':id/edit', element: <InventoryEditPage /> },
        ],
      },
      {
        path: 'inventorylog',
        children: [
          { element: <InventoryLogListPage />, index: true },
          { path: 'list', element: <InventoryLogListPage /> },
        ],
      },
      {
        path: 'fetchlist',
        children: [
          { element: <FetchListPage />, index: true },
          { path: 'list', element: <FetchListPage /> },
          { path: ':id', element: <FetchListDetailsPage /> },
          { path: 'new', element: <FetchListCreatePage /> },
          { path: ':id/edit', element: <FetchListEditPage /> },
          { path: ':id/scan', element: <FetchListScanPage /> },
        ],
      },
      {
        path: 'order',
        children: [
          { element: <OrderListPage />, index: true },
          { path: 'list', element: <OrderListPage /> },
          { path: ':id', element: <OrderDetailsPage /> },
          { path: 'new', element: <OrderCreatePage /> },
          { path: ':id/edit', element: <OrderEditPage /> },
          { path: 'logs', element: <OrderLogListPage /> },
          
        ],
      },
      {
        path: 'invoice',
        children: [
          { element: <InvoiceListPage />, index: true },
          { path: 'list', element: <InvoiceListPage /> },
          { path: ':id', element: <InvoiceDetailsPage /> },
          { path: ':id/edit', element: <InvoiceEditPage /> },
          { path: 'new', element: <InvoiceCreatePage /> },
        ],
      },
      {
        path: 'post',
        children: [
          { element: <BlogPostsPage />, index: true },
          { path: 'list', element: <BlogPostsPage /> },
          { path: ':title', element: <BlogPostPage /> },
          { path: ':title/edit', element: <BlogEditPostPage /> },
          { path: 'new', element: <BlogNewPostPage /> },
        ],
      },
      {
        path: 'job',
        children: [
          { element: <JobListPage />, index: true },
          { path: 'list', element: <JobListPage /> },
          { path: ':id', element: <JobDetailsPage /> },
          { path: 'new', element: <JobCreatePage /> },
          { path: ':id/edit', element: <JobEditPage /> },
        ],
      },
      {
        path: 'tour',
        children: [
          { element: <TourListPage />, index: true },
          { path: 'list', element: <TourListPage /> },
          { path: ':id', element: <TourDetailsPage /> },
          { path: 'new', element: <TourCreatePage /> },
          { path: ':id/edit', element: <TourEditPage /> },
        ],
      },
      {
        path: 'process',
        children: [
          { element: <ProcessListPage />, index: true },
          { path: 'list', element: <ProcessListPage /> },
          { path: 'new', element: <ProcessCreatePage /> },
          { path: ':id/edit', element: <ProcessEditPage /> },
        ],
      },
      {
        path: 'workorder',
        children: [
          { element: <WorkOrderListPage />, index: true },
          { path: 'list', element: <WorkOrderListPage /> },
          { path: 'new', element: <WorkOrderCreatePage /> },
          { path: ':id/edit', element: <WorkOrderEditPage /> },
          { path: ':id/scan', element: <WorkOrderScanPage /> },
        ],
      },
      { path: 'file-manager', element: <FileManagerPage /> },
      { path: 'mail', element: <MailPage /> },
      { path: 'chat', element: <ChatPage /> },
      { path: 'calendar', element: <CalendarPage /> },
      { path: 'kanban', element: <KanbanPage /> },
      { path: 'permission', element: <PermissionDeniedPage /> },
      { path: 'blank', element: <BlankPage /> },
      { path: 'client', element: <ClientPage /> },
      { path: 'vendor', element: <VendorPage /> },
      { path: 'page-configuration', element: <PageConfigurationPage /> },
      { path: 'worker', element: <WorkerPage /> },
      { path: 'measurement', element: <MeasurementPage /> },


    ],
  },
];
